'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { createContext, useContext, useMemo } from 'react';
import {
  type DefaultValues,
  FieldValues,
  FormProvider,
  type FormProviderProps,
  SubmitHandler,
  UseFormReturn,
  useForm as useFormBase,
} from 'react-hook-form';
import { ZodObject, z } from 'zod';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './form';
import { Input, InputProps } from './input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

import React from 'react';

export const localFormContext = createContext({} as UseFormReturn<any>);

export function Form<T extends FieldValues>(
  props: FormProviderProps<T> & {
    className?: HTMLFormProps['className'];
    onSubmit: SubmitHandler<T>;
    children: HTMLFormProps['children'];
  } & {
    formProps?: HTMLFormProps;
  }
) {
  const { formProps, className, onSubmit, children, ...rest } = props;

  return (
    <FormProvider {...rest}>
      <localFormContext.Provider value={rest}>
        <form
          className={className}
          children={children}
          onSubmit={rest.handleSubmit(onSubmit)}
          {...formProps}
        />
      </localFormContext.Provider>
    </FormProvider>
  );
}

type Props = {
  name: string;
  label: string;
  className?: string;
  placeholder?: string;
  inputProps?: InputProps;
  descriptionProps?: {}; // TODO expand as necessary. Derive from respective component
  description?: string;
};

export function FormTextField({
  name,
  inputProps,
  descriptionProps,
  description,
  label,
  className,
  placeholder,
  ...rest
}: Props) {
  const form = useContext(localFormContext);
  return (
    <FormField
      name={name}
      control={form.control}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input placeholder={placeholder} {...inputProps} {...field} />
          </FormControl>
          {description && (
            <FormDescription {...descriptionProps}>
              {description}
            </FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
      {...rest}
    />
  );
}

type FormSelectProps = {
  name: string;
  label: string;
  className?: string;
  placeholder?: string;
  descriptionProps?: {}; // TODO expand as necessary. Derive from respective component
  description?: string;
  items: { value: any; label: React.ReactNode }[];
};

export function FormSelect({
  name,
  descriptionProps,
  description,
  label,
  className,
  placeholder,
  items,
  ...rest
}: FormSelectProps) {
  const form = useContext(localFormContext);
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {items.map(({ label, value }) => {
                return (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <FormDescription {...descriptionProps}>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
      {...rest}
    />
  );
}

export const useForm = <T extends ZodObject<any>>(
  formSchema: T,
  defaultValues: DefaultValues<z.TypeOf<T>> | undefined
) => {
  const form = useFormBase<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  // @ts-ignore
  const keys: Record<
    keyof z.infer<typeof formSchema>,
    keyof z.infer<typeof formSchema>
  > = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(formSchema.shape).map(
          (key: keyof z.infer<typeof formSchema>) => [key, key]
        )
      ),
    [formSchema]
  );

  return { form, keys };
};

export * from './form';

export { z } from 'zod';

type HTMLFormProps = React.ButtonHTMLAttributes<HTMLFormElement>;
